import { z } from "zod";

/**
 * These types have a circular reference, so we need to use z.lazy() to avoid infinite recursion.
 * Because we're using z.lazy, we need to partially define the types manually to avoid an `any` type.
 * @see https://zod.dev/?id=recursive-types
 */

// Base schemas without circular references
const baseHandleSchema = z.object({
  id: z.string(),
  value: z.string(),
  type: z.enum(["emailAddress", "phoneNumber", "address"]),
  urn: z.string(),
  verified: z.boolean().optional(),
  connectorId: z.number().optional(),
  isDefault: z.boolean(),
  isPreferred: z.boolean(),
  /** What the owner calls this address or number — "Work", "Mobile", "Home". */
  label: z.string().optional(),
});

const baseLocationSchema = z.object({
  type: z.enum(["home", "work", "other"]),
  value: z.string(),
  timezoneId: z.string().optional(), // IANA timezone identifier (e.g., "America/Los_Angeles")
  timezoneName: z.string().optional(), // Human-readable timezone name (e.g., "Pacific Standard Time")
});

const baseContactSchema = z.object({
  id: z.string(),
  name: z.string(),
  nickname: z.string().optional(),
  currentTimezone: z.string().optional(),
  currentLocation: z.string().optional(),
  displayFullName: z.string().optional(),
  displayShortName: z.string().optional(),
  picture: z.string().optional(),
  type: z.enum(["person", "company", "assistant", "user"]),
  mergedInto: z.string().optional(), // URN of the contact this was merged into
  connectorId: z.number().optional(),
  accountEmail: z.string().optional(),
  // Time-decayed interaction score, summed across the contact's handles. Optional
  // so the frontend can deploy independently of a backend that sets it.
  decayedInteractionCount: z.number().optional(),
});

/**
 * Sort order for the contacts list.
 *
 * - `frequency` (default) — most-interacted contacts first, by each row's
 *   time-decayed interaction score
 * - `alphabetical` — by name; the order the list endpoint already returns
 */
export const contactListSort = z.enum(["frequency", "alphabetical"]);
export type ContactListSort = z.infer<typeof contactListSort>;
export const DEFAULT_CONTACT_LIST_SORT: ContactListSort = "frequency";

/**
 * The name a new assistant contact is given, and the fallback wherever the
 * assistant's own contact cannot be read. Anything that names the assistant to
 * a recipient should read the contact's name and fall back to this, so that a
 * rename reaches every outgoing message instead of only some of them.
 */
export const DEFAULT_ASSISTANT_NAME = "Okthx";

/** The longest name a user may give their assistant. It goes into email From headers. */
export const ASSISTANT_NAME_MAX_LENGTH = 40;

/** Statsig gate for renaming the assistant: the chat word and the settings section. */
export const RENAME_AGENT_GATE = "rename_agent";

// Types with circular references
export type Contact = z.infer<typeof baseContactSchema> & {
  handles?: Handle[];
  locations?: Location[];
  memories?: {
    id: string;
    content: string;
    category: string;
    anchorDate: string;
    resolvedStartDate?: string;
    resolvedEndDate?: string;
    confirmed: boolean;
  }[];
  favorites?: {
    value: string;
    confidence: number;
    category: string;
    sourceUrns?: string[];
    confirmed: boolean;
  }[];
  relationships?: {
    type: string;
    confidence: number;
    contactUrn: string;
    contactName?: string;
    sourceUrns?: string[];
    confirmed: boolean;
  }[];
};

export type Handle = z.infer<typeof baseHandleSchema> & {
  contact?: Contact;
};

export const createContactLocationReq = baseLocationSchema
  .pick({
    value: true,
    type: true,
  })
  .extend({
    latitude: z.number().optional(),
    longitude: z.number().optional(),
  });

export type CreateContactLocationReq = z.infer<typeof createContactLocationReq>;

export const createContactLocationResp = baseLocationSchema.extend({
  contact: baseContactSchema,
});

export type Location = z.infer<typeof baseLocationSchema> & {
  contact?: Contact;
};

export type CreateContactLocationResp = z.infer<typeof createContactLocationResp>;

export const createContactHandleReq = z.object({
  contactName: z.string(),
  handleType: z.enum(["emailAddress", "phoneNumber"]),
  handleValue: z.string(),
  handleLabel: z.string().trim().optional(),
  accountId: z.number().optional(),
});

export type CreateContactHandleReq = z.infer<typeof createContactHandleReq>;

/** True for "" (which clears the field) or a valid IANA timezone id. */
const isValidTimezone = (tz: string): boolean => {
  if (tz === "") return true;
  try {
    // Throws a RangeError on an unknown/invalid zone id.
    Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
};

/**
 * Edit the base-contact details surfaced in the contact detail sidebar. Every
 * field is optional — only the provided keys are changed. An empty string
 * clears `nickname` / `currentTimezone`; `name` must stay non-empty. A non-empty
 * `currentTimezone` must be a valid IANA id, so an invalid zone can't persist and
 * later throw when it's used for event/time conversion.
 */
export const updateContactReq = z.object({
  name: z.string().trim().min(1).optional(),
  nickname: z.string().optional(),
  currentTimezone: z
    .string()
    .refine(isValidTimezone, { message: "Invalid IANA timezone" })
    .optional(),
});

export type UpdateContactReq = z.infer<typeof updateContactReq>;

/** Add a new email/phone handle to an existing contact. */
export const addContactHandleReq = z.object({
  type: z.enum(["emailAddress", "phoneNumber"]),
  value: z.string().trim().min(1),
  label: z.string().trim().optional(),
});

export type AddContactHandleReq = z.infer<typeof addContactHandleReq>;

/**
 * Edit a handle in place: correct its value, relabel it, or both. Only the
 * provided keys change; an empty `label` clears it. The handle keeps its urn,
 * so the contact keeps the history matched against it.
 */
export const updateContactHandleReq = z.object({
  value: z.string().trim().min(1).optional(),
  label: z.string().trim().optional(),
});

export type UpdateContactHandleReq = z.infer<typeof updateContactHandleReq>;

export const createContactHandleResp = baseHandleSchema.extend({
  contact: baseContactSchema,
});
export type CreateContactHandleResp = z.infer<typeof createContactHandleResp>;

export const syncDeviceContactsReq = z.object({
  deviceName: z.string().max(200).optional(),
  /** Stable per-install id. Absent from clients that predate the split, which
   *  keeps them writing to the legacy-addressed connector. */
  installId: z.string().min(1).max(128).optional(),
  contacts: z.array(
    z.object({
      name: z.string(),
      emails: z.array(z.string()),
      phoneNumbers: z.array(z.string()),
    }),
  ),
});

export type SyncDeviceContactsReq = z.infer<typeof syncDeviceContactsReq>;

/**
 * The tag shown beside a contact's name, or null when it needs none.
 *
 * Most rows are ordinary people, so a "Person" tag would label the default and
 * say nothing. The rest each carry something the name does not: a business, the
 * assistant itself, or the account holder — who reads as "Me", matching what the
 * People list already calls the filter that finds them.
 */
export const contactTypeTag = (type: Contact["type"]): string | null => {
  switch (type) {
    case "person":
      return null;
    case "company":
      return "Company";
    case "assistant":
      return "Assistant";
    case "user":
      return "Me";
  }
};

// Final schemas with circular references
export const handle: z.ZodType<Handle> = baseHandleSchema.extend({
  contact: z.lazy(() => contact).optional(),
});

export const contact: z.ZodType<Contact> = baseContactSchema.extend({
  handles: z.lazy(() => handle.array()).optional(),
});
